import { t } from '@lingui/core/macro';
import { handleResponseErrors } from '@sit/client-shared';
import { NotificationPayload, NotificationTopicSchema, NotificationType } from '@sit/notifications-shared';
import toastr from '../../helpers/toastr';

type MessageMap = Partial<Record<NotificationType, string>>;

const displayToast = (notification: NotificationPayload, messageMap: MessageMap, options = {}): void => {
  const message = messageMap[notification.type];
  if (!message) return;
  const toastId = notification.id || message;

  switch (notification.type) {
    case 'SUCCESS':
      toastr.success(message, { ...options, toastId });
      break;
    case 'WARNING':
      toastr.warning(message, { ...options, toastId });
      break;
    case 'ERROR':
      toastr.error(message, { ...options, toastId });
      break;
    default:
      notification.type satisfies 'INFO';
      toastr.info(message, { ...options, toastId });
  }
};

const toApprovalToastMessages = ({ actionType = 'UNKNOWN', reason }: { actionType: string | undefined; reason?: string }) => {
  switch (actionType) {
    case 'APPROVE': {
      return {
        SUCCESS: t`Successfully approved`,
        ERROR: reason ? t`Failed to approve: ${reason}` : t`Failed to approve`,
      };
    }
    case 'DECLINE': {
      return {
        SUCCESS: t`Successfully declined`,
        ERROR: reason ? t`Failed to decline: ${reason}` : t`Failed to decline`,
      };
    }

    default: {
      return {
        SUCCESS: t`Successfully processed`,
        ERROR: reason ? t`Failed to process: ${reason}` : t`Failed to process`,
      };
    }
  }
};

export const toastNotification = (notification: NotificationPayload): unknown => {
  const { data, topic, type, id } = notification;

  switch (topic) {
    case NotificationTopicSchema.enum.SAVE_TIMESHEET:
      return displayToast(
        notification,
        {
          SUCCESS: t`Your timesheet has been successfully synced to Intacct`,
          ERROR: t`Your timesheet has failed to sync to Intacct`,
        },
        {
          toastId: data?.timesheetId,
        },
      );
    case NotificationTopicSchema.enum.SUBMIT_TIMESHEET:
      return displayToast(
        notification,
        {
          SUCCESS: t`Your timesheet has been successfully submitted`,
          ERROR: t`Your timesheet failed to submit`,
        },
        {
          toastId: data?.timesheetId,
        },
      );
    case NotificationTopicSchema.enum.APPROVALS: {
      const reason = type === 'ERROR' ? handleResponseErrors(data?.reason) : '';
      const toastId = type === 'ERROR' ? reason : id;

      return displayToast(
        notification,
        toApprovalToastMessages({
          actionType: data?.action,
          reason,
        }),
        {
          toastId,
        },
      );
    }
    default:
      return null;
  }
};
